<template>
  <div class="flex w-full flex-col" data-test-id="dashboard-program-progress">
    <!-- Loading state -->
    <LoadingContent v-if="isPendingGroupsRef" />

    <!-- Content with header and objectives -->
    <div v-else class="flex flex-col gap-4 lg:gap-6">
      <!-- Profile Completion Card -->
      <ProfileCompletionCard />

      <!-- Alert mentor en attente d'un mentee -->
      <Alert v-if="showWaitingAlertRef" data-test-id="dashboard-waiting-alert">
        <ClockIcon class="size-4" />
        <AlertTitle>{{ t('dashboard.shared.waitingAlert.title') }}</AlertTitle>
        <AlertDescription>
          {{ t('dashboard.shared.waitingAlert.description') }}
        </AlertDescription>
      </Alert>

      <div class="flex flex-col gap-2">
        <!-- Group Tabs (≤ 4 groupes) -->
        <Tabs
          v-if="userGroupsForProgram.length > 1 && userGroupsForProgram.length <= 4"
          :model-value="selectedGroupIdRef ?? undefined"
          @update:model-value="(val) => dashboardStore.setSelectedGroupId(val as string)"
        >
          <TabsList>
            <TabsTrigger
              v-for="group in userGroupsForProgram"
              :key="group.id"
              :value="group.id"
              data-test-id="group-dropdown"
            >
              {{ group.name }}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <!-- Group Combobox (> 4 groupes) -->
        <Popover v-if="userGroupsForProgram.length > 4" v-model:open="comboboxOpenRef">
          <PopoverTrigger as-child>
            <Button variant="outline" role="combobox" :aria-expanded="comboboxOpenRef" class="w-fit justify-between gap-2">
              {{ selectedGroup?.name }}
              <ChevronsUpDownIcon class="size-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-[250px] p-0">
            <Command>
              <CommandInput :placeholder="t('dashboard.shared.groups.searchPlaceholder')" />
              <CommandList>
                <CommandEmpty>{{ t('dashboard.shared.groups.noGroupFound') }}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    v-for="group in userGroupsForProgram"
                    :key="group.id"
                    :value="group.name"
                    @select="selectComboboxGroup(group.id)"
                  >
                    {{ group.name }}
                    <CheckIcon v-if="selectedGroupIdRef === group.id" class="ml-auto size-4" />
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <!-- Two-column layout: Objectives (left) + Members (right) -->
        <div class="grid grid-cols-1 items-start gap-6 md:grid-cols-[7fr_3fr]">
          <DashboardObjectivesColumn :user-groups="userGroupsForProgram" />
          <DashboardMembersColumn :user-groups="userGroupsForProgram" :selected-group="selectedGroup" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { CheckIcon, ChevronsUpDownIcon, ClockIcon } from 'lucide-vue-next'

import { RoleEnumTypeV2 } from '@/features/auth/types'
import useGroups from '@/features/groups/hooks/use-groups.hook'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import ProfileCompletionCard from '@/features/users/components/profile-completion-card/profile-completion-card.vue'
import DashboardMembersColumn from '@/features/ui/pages/dashboard/dashboard-content/shared/dashboard-members-column/dashboard-members-column.vue'
import DashboardObjectivesColumn from '@/features/ui/pages/dashboard/dashboard-content/shared/dashboard-objectives-column/dashboard-objectives-column.vue'
import { selectGetSelectedGroupId } from '@/features/ui/pages/dashboard/stores/dashboard/dashboard.selectors'
import dashboardStore from '@/features/ui/pages/dashboard/stores/dashboard/dashboard.store'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)

// Waiting alert (mentor or mentee waiting for group assignment)
const { programRolesRef } = useProgramRoles({ programIdRef: selectedProgramIdRef })
const { canFillCriteria, canAutoMatch } = useMatchingSettings(selectedProgramIdRef)
const { isMentorCriteriaCompleteRef, isMenteeCriteriaCompleteRef } = useMatchingInformation({
  onSubmitCallback: () => null,
})

const isMentorRef = computed(() => programRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) ?? false)
const isMenteeRef = computed(() => programRolesRef.value?.includes(RoleEnumTypeV2.MENTEE) ?? false)
const hasMentorCriteriaRef = isMentorCriteriaCompleteRef
const hasMenteeCriteriaRef = isMenteeCriteriaCompleteRef

const showWaitingAlertRef = computed(() => {
  if (userGroupsForProgram.value.length > 0) return false
  if (!canFillCriteria.value) return false

  // Mentor : critères remplis + pas de groupe
  if (isMentorRef.value && hasMentorCriteriaRef.value) return true

  // Mentee : critères remplis + pas de groupe + auto-match désactivé
  if (isMenteeRef.value && hasMenteeCriteriaRef.value && !canAutoMatch.value) return true

  return false
})

// Use dashboard store for selected group
const selectedGroupIdRef = selectGetSelectedGroupId(dashboardStore)

// Get user's groups
const { groupsRef, isPendingRef: isPendingGroupsRef } = useGroups()

// Auto-select first group when groups are loaded
const userGroupsForProgram = computed(() => {
  return groupsRef.value.filter((group) => group.programId === selectedProgramIdRef.value)
})

// Watch for groups changes and auto-select first group
watch(
  userGroupsForProgram,
  (groups) => {
    if (groups.length > 0) {
      const isSelectedGroupValid = groups.some((g) => g.id === selectedGroupIdRef.value)
      if (!selectedGroupIdRef.value || !isSelectedGroupValid) {
        dashboardStore.setSelectedGroupId(groups[0].id)
      }
    }
  },
  { immediate: true },
)

// Get selected group for members display
const selectedGroup = computed(() => {
  return userGroupsForProgram.value.find((group) => group.id === selectedGroupIdRef.value)
})

// Combobox state (for > 4 groups)
const comboboxOpenRef = ref(false)

function selectComboboxGroup(groupId: string) {
  dashboardStore.setSelectedGroupId(groupId)
  comboboxOpenRef.value = false
}


</script>
